/** Pure codecs for preset/URL column state and DRF ordering strings.
 *
 * Column order is applied by REORDERING the `columns` array passed to
 * DataGridPro — v7 rebuilds its internal `orderedFields` from the prop array
 * order on every prop identity change, so this is fully controlled and
 * deterministic (no apiRef.restoreState timing).
 */
import type { GridColDef, GridColumnVisibilityModel, GridSortModel } from '@mui/x-data-grid-pro';

/** Reorder `base` to follow `orderedFields`; fields not mentioned keep their
 *  default relative order, appended at the end. Unknown fields are dropped —
 *  tolerates column-set drift (e.g. BookingsPage's conditional tenant column). */
export function orderColumns<T extends GridColDef>(base: T[], orderedFields: string[]): T[] {
  if (!orderedFields.length) return base;
  const byField = new Map(base.map((c) => [c.field, c]));
  const out: T[] = [];
  for (const field of orderedFields) {
    const col = byField.get(field);
    if (col) {
      out.push(col);
      byField.delete(field);
    }
  }
  for (const col of base) if (byField.has(col.field)) out.push(col);
  return out;
}

/** Visible fields in display order — the `?cols=` URL param payload. */
export function visibleCols(order: string[], visibility: GridColumnVisibilityModel): string[] {
  return order.filter((f) => visibility[f] !== false);
}

export interface ColumnState {
  order: string[];
  visibility: GridColumnVisibilityModel;
}

/** Decode `?cols=a,b,c` (visible fields in display order). Hidden fields are
 *  implied by absence and keep their declared place: each sits right after the
 *  field declared before it (first, when declared first). So a column toggled
 *  on appears where the column menu lists it, not after the last visible one —
 *  and a grid in its declared order keeps exactly that order. */
export function stateFromColsParam(cols: string, defaultOrder: string[]): ColumnState {
  const visible = new Set(cols.split(',').filter((f) => f && defaultOrder.includes(f)));
  const order = [...visible];
  // In declared order, so the field declared before a hidden one is always placed already.
  defaultOrder.forEach((f, i) => {
    if (visible.has(f)) return;
    const prev = i === 0 ? -1 : order.indexOf(defaultOrder[i - 1]!);
    order.splice(prev + 1, 0, f);
  });
  const visibility: GridColumnVisibilityModel = {};
  for (const f of defaultOrder) visibility[f] = visible.has(f);
  return { order, visibility };
}

/** Reconcile a saved preset `columns` section against the current column set:
 *  drop saved fields that no longer exist; append current fields missing from
 *  the saved order (with their default visibility). */
export function reconcileColumns(
  saved: { order: string[]; visibility: Record<string, boolean> },
  defaultOrder: string[],
  defaultHidden: string[] = [],
): ColumnState {
  const known = new Set(defaultOrder);
  const order = saved.order.filter((f) => known.has(f));
  const seen = new Set(order);
  for (const f of defaultOrder) if (!seen.has(f)) order.push(f);
  const visibility: GridColumnVisibilityModel = {};
  for (const f of defaultOrder) {
    visibility[f] =
      f in saved.visibility ? saved.visibility[f] !== false
      : seen.has(f) ? true
      : !defaultHidden.includes(f);
  }
  return { order, visibility };
}

/** Default column state for a grid (no preset, no URL override). */
export function defaultColumnState(
  defaultOrder: string[],
  defaultHidden: string[] = [],
): ColumnState {
  const visibility: GridColumnVisibilityModel = {};
  for (const f of defaultOrder) visibility[f] = !defaultHidden.includes(f);
  return { order: [...defaultOrder], visibility };
}

/** DRF ordering string ("-a,b") ↔ GridSortModel. */
export function orderingToSortModel(ordering: string): GridSortModel {
  return ordering
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
    .map((s) =>
      s.startsWith('-') ? { field: s.slice(1), sort: 'desc' as const } : { field: s, sort: 'asc' as const },
    );
}

export function sortModelToOrdering(model: GridSortModel): string {
  return model
    .filter((s) => s.sort)
    .map((s) => `${s.sort === 'desc' ? '-' : ''}${s.field}`)
    .join(',');
}
